import { reportClientErrorOnce } from '@/lib/clientErrorLog';

/**
 * Registrazione lato client dell'audio di chiamata (locale + remoto mixati)
 * per la trascrizione server-side.
 *
 * iOS Safari / WKWebView non supporta audio/webm: il MediaRecorder va
 * costruito con un mimeType realmente supportato (audio/mp4 su iOS) o senza
 * mimeType (default del browser), altrimenti il costruttore lancia
 * NotSupportedError e la chiamata resta senza registrazione né trascrizione.
 */

const CANDIDATE_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
];

export function pickRecordingMimeType(
  isTypeSupported: ((type: string) => boolean) | undefined = typeof MediaRecorder !== 'undefined'
    ? MediaRecorder.isTypeSupported?.bind(MediaRecorder)
    : undefined,
): string | undefined {
  if (!isTypeSupported) return undefined;
  return CANDIDATE_MIME_TYPES.find((type) => {
    try {
      return isTypeSupported(type);
    } catch {
      return false;
    }
  });
}

// Estensione file per mime base: il server (multer) salva col nome originale
// e la trascrizione deduce il mime dall'estensione — .m4a → audio/mp4.
const EXTENSION_BY_MIME: Record<string, string> = {
  'audio/webm': 'webm',
  'audio/mp4': 'm4a',
  'audio/ogg': 'ogg',
  'audio/wav': 'wav',
  'audio/mpeg': 'mp3',
};

/**
 * Normalizza il mimeType effettivo del recorder per l'upload: il filtro
 * multer confronta la stringa esatta, quindi "audio/mp4;codecs=..." va
 * ridotto a "audio/mp4".
 */
export function recordingUploadInfo(recorderMimeType: string | undefined): {
  blobType: string;
  fileName: string;
} {
  const base = (recorderMimeType || '').split(';')[0]!.trim().toLowerCase();
  const blobType = EXTENSION_BY_MIME[base] ? base : 'audio/webm';
  return { blobType, fileName: `recording.${EXTENSION_BY_MIME[blobType]}` };
}

export interface ActiveCallRecording {
  /** Ferma il recorder e restituisce l'audio pronto per l'upload (null se vuoto). */
  stop(): Promise<{ blob: Blob; fileName: string } | null>;
}

export interface CallRecordingOptions {
  /**
   * Upload incrementale dei byte registrati: chiamato periodicamente con i
   * byte non ancora inviati e l'offset atteso dal server. Deve risolvere col
   * size totale ora presente sul server (usato per riallinearsi dopo un
   * restart del server o un upload andato a buon fine solo a metà). Se la
   * pagina muore prima di call:ended, il server ha comunque l'audio fino
   * all'ultimo flush.
   */
  uploadChunk?: (chunk: Blob, offset: number, fileName: string) => Promise<number>;
  flushIntervalMs?: number;
}

const DEFAULT_FLUSH_INTERVAL_MS = 20_000;

export function startCallRecording(
  localStream: MediaStream,
  remoteStream: MediaStream,
  callSessionId: string,
  options?: CallRecordingOptions,
): ActiveCallRecording | null {
  try {
    const ctx = new AudioContext();
    // Fuori dal gesto utente (partiamo su 'connected') iOS può creare il
    // context 'suspended': senza resume registreremmo solo silenzio.
    if (ctx.state !== 'running') void ctx.resume().catch(() => {});

    const dest = ctx.createMediaStreamDestination();
    ctx.createMediaStreamSource(localStream).connect(dest);
    ctx.createMediaStreamSource(remoteStream).connect(dest);

    const mimeType = pickRecordingMimeType();
    const recorder = mimeType
      ? new MediaRecorder(dest.stream, { mimeType })
      : new MediaRecorder(dest.stream);

    const chunks: Blob[] = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };
    recorder.start(1000); // raccogli dati ogni secondo

    // ---- Flush incrementale verso il server (best-effort) ----
    const uploadChunk = options?.uploadChunk;
    let uploadedBytes = 0;
    let flushing = false;

    async function flush(): Promise<void> {
      if (!uploadChunk || flushing) return;
      const { blobType, fileName } = recordingUploadInfo(recorder.mimeType);
      const full = new Blob(chunks, { type: blobType });
      if (full.size <= uploadedBytes) return;
      flushing = true;
      try {
        const pending = full.slice(uploadedBytes, full.size, blobType);
        // Il server risponde col suo size totale: è LUI la fonte di verità
        // (dopo un suo restart il part file può essere più corto del nostro
        // contatore). Un errore lascia l'offset invariato: ritenta al tick.
        uploadedBytes = await uploadChunk(pending, uploadedBytes, fileName);
      } catch {
        // ritenta al prossimo flush
      } finally {
        flushing = false;
      }
    }

    const flushTimer = uploadChunk
      ? setInterval(() => void flush(), options?.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS)
      : null;

    return {
      stop() {
        return new Promise((resolve) => {
          if (flushTimer) clearInterval(flushTimer);
          if (recorder.state === 'inactive') {
            void ctx.close().catch(() => {});
            resolve(null);
            return;
          }
          recorder.onstop = () => {
            void ctx.close().catch(() => {});
            const { blobType, fileName } = recordingUploadInfo(recorder.mimeType);
            const blob = new Blob(chunks, { type: blobType });
            // Ultimo flush con la coda: se l'upload finale poi fallisce, il
            // part file sul server resta comunque completo.
            void flush();
            if (blob.size > 0) {
              resolve({ blob, fileName });
            } else {
              // Registrazione partita ma zero byte raccolti (es. AudioContext
              // sospeso in una WebView): prima veniva scartato in silenzio.
              reportClientErrorOnce(`call-recording-empty-${callSessionId}`, {
                code: 'CALL_RECORDING_EMPTY',
                message:
                  'Registrazione chiamata vuota (0 byte): trascrizione non disponibile',
                context: { callSessionId, mimeType: recorder.mimeType || 'unknown' },
              });
              resolve(null);
            }
          };
          recorder.stop();
        });
      },
    };
  } catch (err) {
    // Senza registrazione la chiamata funziona comunque, ma la trascrizione
    // non esisterà: va tracciato nel Log Errori, non solo in console.
    reportClientErrorOnce(`call-recording-start-${callSessionId}`, {
      code: 'CALL_RECORDING_FAILED',
      message: 'Registrazione chiamata non avviata: la trascrizione non sarà disponibile',
      context: {
        callSessionId,
        error: err instanceof Error ? `${err.name}: ${err.message}` : String(err),
      },
    });
    return null;
  }
}
